import { useMemo, useState } from "react";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { RiArrowDropDownLine } from "@remixicon/react";

import InputFieldLabel from "components/InputFieldComponents/InputFieldLabel";
import InputFieldWithValidation from "components/InputFieldWithValidation";
import { cx } from "utils";

import { INPUT_CLASS } from "./BillingAddressForm";


interface BillingComboBoxProps<T> {
  name?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  requiredErrorMessage?: string;
  errorMessage?: string;
  options: T[];
  inputDisplayValue?: (item: T) => string;
  listValue?: (item: T) => string;
  value?: T;
  onOptionSelect?: (item: T) => void;
}

export default function BillingComboBox<T>({
  name,
  label,
  placeholder,
  required,
  requiredErrorMessage,
  errorMessage,
  options,
  inputDisplayValue,
  listValue,
  value,
  onOptionSelect,
}: BillingComboBoxProps<T>) {
  const [selection, setSelection] = useState<T | undefined>(value);
  const [query, setQuery] = useState("");
  const [revertSignal, setRevertSignal] = useState(0);

  const handleSelection = (selected: T) => {
    setSelection(selected);
    onOptionSelect?.(selected);
  };

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((option) => {
      const display = inputDisplayValue ? inputDisplayValue(option) : String(option);
      return display.toLowerCase().includes(q);
    });
  }, [query, options, inputDisplayValue]);

  return (
    <Combobox
      name={name}
      value={selection}
      onChange={handleSelection}
      onClose={() => {
        setQuery("");
        setRevertSignal((k) => k + 1);
      }}
      immediate
    >
      {({ open }) => (
        <div className="relative">
          <ComboboxInput
            as={InputFieldWithValidation}
            label={ <InputFieldLabel label={ label } required={ required } /> }
            required={required}
            requiredErrorMessage={requiredErrorMessage}
            errorMessage={errorMessage}
            placeholder={placeholder}
            className={INPUT_CLASS}
            displayValue={inputDisplayValue}
            value={selection && inputDisplayValue ? inputDisplayValue(selection) : ""}
            resetKey={revertSignal}
            isValidated={revertSignal > 0 || errorMessage !== undefined}
            onChange={(e: any) => setQuery(e.target.value)}
          />
          <ComboboxButton className="absolute right-0 bottom-[20px] flex items-center pr-2 h-[38px]">
            <RiArrowDropDownLine className={cx("w-5 h-5 text-gray-600 transition-transform", open && "rotate-180")} />
          </ComboboxButton>
          <ComboboxOptions className="absolute w-full overflow-y-auto max-h-52 z-40 bg-white border border-neutral-900 rounded p-1 drop-shadow -mt-[22px]">
            {filteredOptions.length === 0 ? (
              <div className="py-2 px-3 text-sm text-gray-500 italic">No results found</div>
            ) : (
              filteredOptions.map((option) => (
                <ComboboxOption
                  key={listValue?.(option) || String(option)}
                  value={option}
                  className={cx(
                    "py-1.5 px-3 text-sm cursor-pointer transition-colors duration-150",
                    "data-[selected]:!bg-primary-600 data-[selected]:text-white",
                    "data-[focus]:bg-primary-600 data-[focus]:text-white"
                  )}
                >
                  {listValue?.(option) || String(option)}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      )}
    </Combobox>
  );
}
